import React, { useEffect } from 'react';
import {
    FlaskConical,
    Radar,
    Bot,
    CameraOff,
    Hourglass,
    MessageSquare,
    Compass,
    Layers,
    FileText,
    Palette,
    BarChart3,
    Filter,
    Handshake,
    ListChecks,
    Video,
    Eye,
    PhoneCall,
    CheckCircle2,
    XCircle
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function FarazPersonalBrand() {

    useEffect(() => {
        document.title = "Faraz Ansari | Founder Brand Engine";
        window.scrollTo(0, 0);
    }, []);

    const steps = [
        { n: '01', h: 'You fill the questionnaire', s: 'Your own copy of the doc is linked at the bottom of this page. About 20 minutes. Answer it yourself, not with AI: the strategy is only as good as this input.' },
        { n: '02', h: 'We research, then put a number in writing', s: 'Your ICP, your offer, the guarantee we can honestly back and the exact monthly fee. You decide with everything on the table.' },
        { n: '03', h: 'Agreement, then 7-day onboarding', s: 'Clone training data, positioning, content pillars and the first scripts. Back and forth is heaviest here, on purpose.' },
        { n: '04', h: 'The engine runs', s: 'Posting, the AI setter working your DMs, the live dashboard. Your calendar starts filling with qualified calls.' }
    ];

    const wont = [
        { h: 'A price before the research', s: 'The number depends on your ICP and the guarantee we can honestly back. It comes in writing after the questionnaire, exactly as discussed.' },
        { h: 'Views for their own sake', s: 'Niche content reads low on views by design. The metric is who it reaches and what lands in your DMs.' },
        { h: 'Your middle and bottom funnel', s: 'We bring the horse to the water. Qualifying happens automatically, closing and delivery stay yours.' }
    ];

    const will = [
        { h: 'A minimum guarantee in writing', s: '10,000 followers in 90 days, or the metric that fits your funnel better. Miss it and 100% of your money comes back, in a legally bound contract.' },
        { h: 'The in-house escape hatch', s: 'If after three months you would rather build it internally, we hand off the complete system and train your team. We have done it before.' },
        { h: 'Your answer inside 15 days', s: 'You said you know within 15 days whether something adds value. The cadence starts inside week 2, so you will have your answer early.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Faraz Ansari</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The engineer they trust<br />when everything is hype.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        A founder brand for Faraz Ansari that compounds across every experiment you run.<br className="hidden sm:block" /> Built on your AI clone, run end to end by us, while you keep building the business.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · Monthly</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">$1,000 to $2,000</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">per month · 3-month engagement · final number and guarantee locked in writing after research</div>
                        </div>
                    </div>
                </section>

                {/* THE PROCESS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The process</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From yes<br />to a running engine.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You asked when the money moment comes. Here is the full order of operations, so nothing is locked in before you have the number.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {steps.map((step) => (
                            <div key={step.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{step.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{step.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{step.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where you are right now.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={FlaskConical} heading="Every experiment starts at zero" subheading="Storybook AI, call-center QA, healthcare · each new bet launches with no audience to sell to" accent="rose" />
                        <InfoCard icon={Radar} heading="The hype wave has no trusted face" subheading="Buyers know they are missing out on AI · they cannot tell real builders from course-sellers" accent="rose" />
                        <InfoCard icon={Bot} heading="The DIY clone fought back" subheading="You tried it yourself: lip sync off, impressions exaggerated · your words, not ours" accent="rose" />
                        <InfoCard icon={CameraOff} heading="No pipeline while you build" subheading="Content stops the moment product work starts · consistency is the first casualty" accent="rose" />
                        <InfoCard icon={Hourglass} heading="Your time is the product" subheading="Every hour on scripting and editing is an hour taken from delivery and customers" accent="rose" />
                        <InfoCard icon={MessageSquare} heading="A general pitch invites DIY" subheading="You said it on the call: pitch broad and they answer, I can do this myself · depth is the moat" accent="rose" />
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we build and run.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Compass} heading="ICP and offer research first" subheading="Who to target, broad or deep, what the one clear offer is · the questions you asked, answered before content starts" accent="indigo" />
                        <InfoCard icon={Layers} heading="One brand, all your pillars" subheading="The e-com exit, the automations, healthcare · positioned as the technologist who was here before the bubble" accent="indigo" />
                        <InfoCard icon={Bot} heading="Your AI clone, done right" subheading="Our own fine-tuned LoRA stack for Indian skin tones and accent · one training session, then volume without you" accent="indigo" />
                        <InfoCard icon={FileText} heading="Complete done for you" subheading="Ideation, scripting, research, recording, editing, posting, management" accent="indigo" />
                        <InfoCard icon={Palette} heading="A brand identity that holds" subheading="You noticed the consistency on our client pages · the same system, built for you" accent="indigo" />
                        <InfoCard icon={BarChart3} heading="6,000 posts of data behind every script" subheading="We track everything we publish · what works in one niche gets rebuilt for yours" accent="indigo" />
                        <InfoCard icon={Filter} heading="Comments become qualified calls" subheading="The Meta-verified AI setter qualifies DMs in your voice, follows up and books the calls" accent="indigo" />
                        <InfoCard icon={Handshake} heading="Top of funnel only" subheading="Eyeballs, leads and booked calls from us · the middle, the bottom and the closing stay yours" accent="indigo" />
                    </div>
                </section>

                {/* YOUR SIDE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we need from you.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Lighter than building it in-house. No editor to hire, no script writer, no social media manager.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={ListChecks} heading="20 minutes, yourself" subheading="The questionnaire works when it is you answering, not an AI · the strategy is built on it" accent="emerald" />
                        <InfoCard icon={Video} heading="One clone training session" subheading="A single recording session covers months of content" accent="emerald" />
                        <InfoCard icon={Eye} heading="Approve before it ships" subheading="Verify videos in the early weeks · most founders go hands-off once the rhythm sets" accent="emerald" />
                        <InfoCard icon={PhoneCall} heading="Take the booked calls" subheading="Qualified people land on your calendar · closing stays yours" accent="emerald" />
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The exchange</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        The questionnaire is the next step.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        This is your own copy, just open and fill it. 20 minutes, answered by you, and the research starts the same day.
                    </p>
                    <a
                        href="https://docs.google.com/document/d/176FYTvnEAbZkmj3nzoZ78RYZb5S3eTPFnF6-zRFkwo0/edit"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        Open your questionnaire
                    </a>
                    <div className="mt-6">
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition text-sm">or write to suresh@socialseo.in</a>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
